import * as React from 'react'
import {
  Badge,
  Card,
  CardContent,
  colors,
  Flex,
  Heading,
  settings,
  settings as settingsStyles,
  Paragraph,
  StyledText,
  useTheme,
} from '@dooer/react-ui-kit'
import {
  AcademyCategoryIcon as AcademyCategoryIconType,
  AcademyCustomerCategory,
} from '../../__generated__/globalTypes'
import { IAcademyArticle, IAcademyCategory } from './index'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/pro-light-svg-icons'
import { Link } from 'react-router-dom'
import AcademyCategoryIcon, { iconStyles } from './academy-category-icon'
import { FormattedMessage } from 'react-intl'

interface IAcademyListItemProps {
  title: string
  icon: AcademyCategoryIconType | null
  categoryId?: string
  articleId?: string
  last: boolean
  count?: number
  path?: string | null
  iconStyle?: keyof typeof iconStyles
  academySearch?: string | null
  isSearch?: boolean
}

const AcademyListItem = (props: IAcademyListItemProps) => {
  const theme = useTheme()

  return (
    <Link
      style={{ textDecoration: 'none' }}
      to={[`/hjalp/${props.path}`, props.academySearch ? `?academySearch=${props.academySearch}` : ''].join('')}
    >
      <Card style={{ boxShadow: settings.shadows.elevation[2] }} tight>
        <CardContent flush>
          <Flex
            alignItems="center"
            flexGrow={1}
            justifyContent="space-between"
            marginBottom="large"
            marginLeft="large"
            marginRight="large"
            marginTop="large"
            row
          >
            <Flex alignItems="center" justifyContent="flex-start" row>
              {props.icon && (
                <AcademyCategoryIcon category={{ icon: props.icon }} iconStyle={props.iconStyle || 'default'} />
              )}
              {props.isSearch ? (
                <StyledText
                  style={{
                    fontFamily: 'aktiv-grotesk',
                    marginLeft: settingsStyles.spacing.small,
                    fontWeight: 500,
                    color: theme.colors.text.normalActive,
                  }}
                >
                  {props.title}
                </StyledText>
              ) : (
                <Heading flush level="h2" style={{ marginLeft: settingsStyles.spacing.small }}>
                  {props.title}
                </Heading>
              )}
              {props.count && props.count !== 0 ? (
                <Flex marginLeft="small">
                  <Badge style={{ background: colors.accents.brand, color: colors.text.light, border: 'none' }}>
                    <FormattedMessage
                      defaultMessage="{totalCount} artiklar"
                      id="academy.search-result.total-count"
                      values={{ totalCount: props.count }}
                    />
                  </Badge>
                </Flex>
              ) : null}
            </Flex>
            <Flex paddingLeft="medium">
              <FontAwesomeIcon icon={faChevronRight} />
            </Flex>
          </Flex>
        </CardContent>
      </Card>
    </Link>
  )
}

interface IAcademyRootListItemProps {
  title: string
  description?: string | null
  icon: AcademyCategoryIconType | null
  categoryId?: string
  articleId?: string
  last: boolean
  count?: number
  path?: string | null
  iconStyle?: keyof typeof iconStyles
  academySearch?: string | null
}

const AcademyRootListItem = (props: IAcademyRootListItemProps) => {
  return (
    <div>
      <Link
        style={{ textDecoration: 'none' }}
        to={[`/hjalp/${props.path}`, props.academySearch ? `?academySearch=${props.academySearch}` : ''].join('')}
      >
        <Flex height="100%" marginRight="medium" minHeight={200} width={290}>
          <Card style={{ boxShadow: settings.shadows.elevation[2] }} tight>
            <CardContent flush>
              <Flex marginBottom="large" marginLeft="large" marginRight="large" marginTop="large">
                <Flex marginBottom="medium" row>
                  {props.count && props.count !== 0 ? (
                    <Badge style={{}} theme="darkGray">
                      <FormattedMessage
                        defaultMessage="{totalCount} artiklar"
                        id="academy.search-result.total-count"
                        values={{ totalCount: props.count }}
                      />
                    </Badge>
                  ) : (
                    <Flex height={20} />
                  )}
                </Flex>
                <Flex alignItems="center" justifyContent="flex-start" row>
                  {props.icon && (
                    <AcademyCategoryIcon category={{ icon: props.icon }} iconStyle={props.iconStyle || 'default'} />
                  )}
                  <Heading level="h2" style={{ marginLeft: settingsStyles.spacing.small, marginBottom: 0 }}>
                    {props.title}
                  </Heading>
                </Flex>
                {props.description && (
                  <Paragraph flush style={{ fontFamily: 'aktiv-grotesk' }}>
                    {props.description}
                  </Paragraph>
                )}
              </Flex>
            </CardContent>
          </Card>
        </Flex>
      </Link>
    </div>
  )
}

interface IAcademyListProps {
  articles?: IAcademyArticle[]
  categories?: IAcademyCategory[]
  customerCategory?: AcademyCustomerCategory
  iconStyle?: keyof typeof iconStyles
  academySearch?: string | null
  isRoot: boolean
  isSearch?: boolean
}

export const AcademyList = (props: IAcademyListProps) => {
  const ListItem = props.isRoot ? AcademyRootListItem : AcademyListItem

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterCustomerCategory = (node: any) =>
    props.customerCategory && node.customerCategories?.includes(props.customerCategory)

  return (
    <>
      {props.articles
        ? props.articles.map((article, i) => (
            <ListItem
              academySearch={props.academySearch}
              articleId={article.id}
              categoryId={(article.category && article.category.id) || undefined}
              description={article.description}
              icon={article.icon}
              iconStyle={props.iconStyle}
              isSearch={props.isSearch}
              key={article.id}
              last={i === (props.articles && props.articles.length)}
              path={article.path}
              title={article.title}
            />
          ))
        : null}
      {props.categories
        ? props.categories.filter(filterCustomerCategory).map((category, i) =>
            props.isRoot ? (
              <ListItem
                academySearch={props.academySearch}
                categoryId={category.id}
                count={
                  (category.children?.edges || []).reduce(
                    (sum, edge) =>
                      sum +
                      (edge.node.articles?.edges?.filter(({ node }) => filterCustomerCategory(node))?.length || 0),
                    0
                  ) +
                  ((category.articles &&
                    category.articles.edges.filter(({ node }) => filterCustomerCategory(node)).length) ||
                    0)
                }
                description={category.description}
                icon={category.icon}
                iconStyle={props.iconStyle}
                key={category.id}
                last={i === (props.categories && props.categories.length)}
                path={category.path}
                title={category.title}
              />
            ) : (
              <Flex key={category.id} marginTop="xxLarge">
                <Heading level="h2">{category.title}</Heading>
                {(category?.articlesWithOrder || []).filter(filterCustomerCategory).map((article) => (
                  <ListItem
                    academySearch={props.academySearch}
                    articleId={article.id}
                    categoryId={(article.category && article.category.id) || undefined}
                    description={article.description}
                    icon={article.icon}
                    iconStyle={props.iconStyle}
                    isSearch={props.isSearch}
                    key={article.id}
                    last={i === (props.articles && props.articles.length)}
                    path={article.path}
                    title={article.title}
                  />
                ))}
              </Flex>
            )
          )
        : null}
    </>
  )
}
